import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSingleObject } from '../../../reducers/documentation/documentationReducer';
import {
  selectParsedFields,
  selectRelatedObjectsByObjectName,
} from '../../../reducers/global/parserReducer';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { updateStructureWithParentElementType } from './filters/utils';
import { useDocumentation } from '../useDocumentation';
import { ruleByObjectName } from '../../parser/parserUtils';
import groupBy from 'lodash/groupBy';
import { createConfigurationItemFromAutomation } from '../helpers';
import { ConfigurationItem } from '../../parser/ParserTypes';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import { useGetDeployedVersions } from './useGetDeployedVersions';
import {
  SWEEP_AUTOMATION,
  SWEEP_ALERT,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  SWEEP_DEDUPE,
  SWEEP_MATCHING,
} from '../../parser/ConfigurationItemTypes';

export const usePrepareActiveObjectStructure = (
  crmOrgId: string,
): { [key in DocumentationTabTypes]: ConfigurationItem[] } => {
  const objectTypeName = useSelector(selectSingleObject);
  const _fields = useSelector(selectParsedFields(crmOrgId))?.[objectTypeName?.objectType ?? ''];
  const relatedObjects = useSelector(selectRelatedObjectsByObjectName(crmOrgId))?.[
    objectTypeName?.objectType ?? ''
  ];

  const users = useSelector(selectAccountUsers);

  const { configurationItems, parsedRecordTypes } = useDocumentation();
  const _parsedRecordTypes = useMemo(
    () =>
      parsedRecordTypes?.filter(
        (rt) =>
          rt.objectApiNames.includes(objectTypeName?.objectType ?? '') && rt.name !== '__MASTER__',
      ) ?? [],
    [parsedRecordTypes, objectTypeName],
  );

  const deployedVersions = useGetDeployedVersions(crmOrgId);

  const objectConfigurationItems = useMemo(() => {
    const filtered = configurationItems.filter(ruleByObjectName(objectTypeName?.objectType ?? ''));
    return filtered;
  }, [configurationItems, objectTypeName]);

  const groupedByType = useMemo(
    () => groupBy(objectConfigurationItems, 'parentType'),
    [objectConfigurationItems],
  );

  const {
    Flow,
    ApprovalProcess,
    ProcessBuilderFlow,
    WorkflowRule,
    Layout,
    ValidationRule,
    ApexTrigger,
    ApexClass,
  } = groupedByType;

  const deployedSweepAutomations = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_AUTOMATION,
        objectTypeName?.objectType ?? '',
        deployedVersions.automations,
      ),
    [objectTypeName?.objectType, deployedVersions.automations],
  );

  const deployedSweepAlerts = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_ALERT,
        objectTypeName?.objectType ?? '',
        deployedVersions.alerts,
      ),
    [objectTypeName?.objectType, deployedVersions.alerts],
  );

  const deployedAssignments = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_ASSIGNMENT,
        objectTypeName?.objectType ?? '',
        deployedVersions.assignments,
      ),
    [objectTypeName?.objectType, deployedVersions.assignments],
  );

  const deployedScheduledAssignments = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_SCHEDULED_ASSIGNMENT,
        objectTypeName?.objectType ?? '',
        deployedVersions.scheduledAssignments,
      ),
    [objectTypeName?.objectType, deployedVersions.scheduledAssignments],
  );

  const dedupeNewStructure = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_DEDUPE,
        objectTypeName?.objectType ?? '',
        deployedVersions.dedup,
      ),
    [objectTypeName?.objectType, deployedVersions.dedup],
  );

  const matchingNewStructure = useMemo(
    () =>
      updateStructureWithParentElementType(
        SWEEP_MATCHING,
        objectTypeName?.objectType ?? '',
        deployedVersions.matching,
      ),
    [objectTypeName?.objectType, deployedVersions.matching],
  );

  const list = useMemo(() => {
    const sfAutomations = [
      ...(Flow ?? []),
      ...(ApprovalProcess ?? []),
      ...(WorkflowRule ?? []),
      ...(ProcessBuilderFlow ?? []),
    ];
    const valRules = ValidationRule ?? [];
    return {
      [DocumentationTabTypes.FIELDS]: _fields ?? [],
      [DocumentationTabTypes.RECORD_TYPES]: _parsedRecordTypes ?? [],
      [DocumentationTabTypes.SF_AUTOMATIONS]: sfAutomations,
      [DocumentationTabTypes.LAYOUTS]: Layout ?? [],
      [DocumentationTabTypes.VALIDATION_RULES]: valRules,
      [DocumentationTabTypes.APEX]: [...(ApexClass ?? []), ...(ApexTrigger ?? [])],
      [DocumentationTabTypes.MATCHING_DEDUPE]: [...matchingNewStructure, ...dedupeNewStructure].map(
        (automation) => createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.PLAYBOOK_ALERTS]: deployedSweepAlerts.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.ASSIGNMENTS]: deployedAssignments.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]: deployedScheduledAssignments.map(
        (automation) => createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.SWEEP_AUTOMATIONS]: deployedSweepAutomations.map((automation) =>
        createConfigurationItemFromAutomation(automation, users),
      ) as ConfigurationItem[],
      [DocumentationTabTypes.CARDS_LIST]: [],
      [DocumentationTabTypes.RELATED_OBJECTS]: relatedObjects ?? [],
      [DocumentationTabTypes.OTHER]: [],
    };
  }, [
    _fields,
    _parsedRecordTypes,
    ProcessBuilderFlow,
    WorkflowRule,
    Flow,
    ApprovalProcess,
    Layout,
    ValidationRule,
    ApexTrigger,
    dedupeNewStructure,
    matchingNewStructure,
    deployedAssignments,
    deployedScheduledAssignments,
    deployedSweepAutomations,
    deployedSweepAlerts,
    users,
    relatedObjects,
    ApexClass,
  ]);

  return list;
};
